/**
 * ImageInputModule
 *
 * A jQuery-based module for creating and managing image input UI components.
 * Handles file selection, preview, cancellation, and removal with support
 * for custom events and callbacks.
 *
 * @namespace ImageInputModule
 */

/**
 * Initialize the image input module with the provided configuration.
 *
 * @function init
 * @memberof ImageInputModule
 * @param {Object} [config={}] - Configuration options for the image input
 * @param {string} [config.targetSelector='.image-input'] - Selector for target elements to transform into image inputs
 * @param {string} [config.wrapperSelector='.image-input-wrapper'] - Selector for the image wrapper element
 * @param {string} [config.inputSelector='input[type="file"]'] - Selector for the file input element
 * @param {string} [config.cancelSelector='[data-js-image-input-action="cancel"]'] - Selector for the cancel button
 * @param {string} [config.removeSelector='[data-js-image-input-action="remove"]'] - Selector for the remove button
 * @param {string} [config.hiddenInputSelector='input[type="hidden"]'] - Selector for the hidden input element
 * @param {Function} [config.onInitComplete=null] - Callback function to execute when initialization is complete
 * @returns {Array} An array of initialized instances, each containing target, elements and config
 *
 * @example
 * // Basic initialization with default options
 * const instances = ImageInputModule.init();
 *
 * @example
 * // Initialization with custom options
 * const instances = ImageInputModule.init({
 *     targetSelector: '#profile-image',
 *     onInitComplete: function(instances) {
 *         console.log('Initialization complete', instances);
 *     }
 * });
 */

const createImageInputModule = function($) {
    /**
     * Get all the relevant DOM elements
     */
    const getDomElements = (config, $targetElement) => {
        return {
            element: $targetElement[0],
            inputElement: $targetElement.find(config.inputSelector)[0],
            wrapperElement: $targetElement.find(config.wrapperSelector)[0],
            cancelElement: $targetElement.find(config.cancelSelector)[0],
            removeElement: $targetElement.find(config.removeSelector)[0],
            hiddenElement: $targetElement.find(config.hiddenInputSelector)[0]
        };
    };

    /**
     * Set up event handlers for the image input
     */
    const setupEventHandlers = (elements, config) => {
        const { inputElement, cancelElement, removeElement } = elements;

        // Add change event handler to input element
        if (inputElement) {
            $(inputElement).off('change').on('change', function(e) {
                handleChange(e, elements, config);
            });
        }

        // Add click event handler to cancel element
        if (cancelElement) {
            $(cancelElement).off('click').on('click', function(e) {
                handleCancel(e, elements, config);
            });
        }

        // Add click event handler to remove element
        if (removeElement) {
            $(removeElement).off('click').on('click', function(e) {
                handleRemove(e, elements, config);
            });
        }
    };

    /**
     * Handle file input change event
     */
    const handleChange = (e, elements, config) => {
        e.preventDefault();
        const { element, inputElement, wrapperElement } = elements;

        if (inputElement !== null && inputElement.files && inputElement.files[0]) {
            if (!triggerEvent(element, 'js.imageinput.change', elements)) return;

            const reader = new FileReader();
            const file = inputElement.files[0];

            reader.onload = function (e) {
                wrapperElement.style.backgroundImage = `url(${e.target.result})`;
                // Save the image base64 to element dataset
                element.dataset.previewImage = e.target.result;
            };

            reader.readAsDataURL(file);

            element.classList.add('image-input-changed');
            element.classList.remove('image-input-empty');

            // Fire after changed event
            triggerEvent(element, 'js.imageinput.changed', elements);
        }
    };

    /**
     * Handle cancel button click event
     */
    const handleCancel = (e, elements, config) => {
        e.preventDefault();

        const { element, wrapperElement, inputElement, hiddenElement } = elements;
        const originalSrc = $(element).data('original-src') || 'none';

        if (!triggerEvent(element, 'js.imageinput.cancel', elements)) return;

        element.classList.remove('image-input-changed');
        element.classList.remove('image-input-empty');

        wrapperElement.style.backgroundImage = (originalSrc === 'none') ? '' : originalSrc;
        if (originalSrc === 'none') element.classList.add('image-input-empty');

        inputElement.value = '';
        if (hiddenElement) hiddenElement.value = '0';

        triggerEvent(element, 'js.imageinput.canceled', elements);
    };

    /**
     * Handle remove button click event
     */
    const handleRemove = (e, elements, config) => {
        e.preventDefault();
        const { element, wrapperElement, inputElement, hiddenElement } = elements;

        if (!triggerEvent(element, 'js.imageinput.remove', elements)) return;

        element.classList.remove('image-input-changed');
        element.classList.add('image-input-empty');

        wrapperElement.style.backgroundImage = 'none';
        inputElement.value = '';
        if (hiddenElement) hiddenElement.value = '1';

        triggerEvent(element, 'js.imageinput.removed', elements);
    };

    /**
     * Helper function to trigger custom events
     */
    const triggerEvent = (element, name, data) => {
        const event = new CustomEvent(name, {
            bubbles: true,
            cancelable: true,
            detail: data
        });

        return element.dispatchEvent(event);
    };

    /**
     * Initialize the component state
     */
    const initializeState = (elements, config) => {
        const { element, wrapperElement } = elements;

        // Store original background image
        const src = window.getComputedStyle(wrapperElement).backgroundImage || 'none';

        $(element).data('original-src', src);
        element.setAttribute('data-js-image-input', 'true');

        // Check if empty initially
        if (src === 'none' || src === '' || src === 'url("")' || src === 'url()') {
            element.classList.add('image-input-empty');
        }
    };

    const _initImageTabSwitching = () => {
        $('.glyph-image-tabs .nav-tabs a:not(.image-dimensions)').off('click').on('click', function (e) {
            e.preventDefault();
            const $elem = $(this);
            const $parent = $elem.closest(".glyph-image-tabs");
            const uploader = parseInt($elem.data("uploader"));

            $parent.find(".tab-content input").prop("disabled", true);
            $parent.find(".image_uploader").val(uploader);

            $($elem.attr("href")).find("input").prop("disabled", false);
            $elem.tab('show');
        });
    };

    const _initImageDeleteHandlers = () => {
        $(document).off('click', 'a.deletePreviewImage').on('click', 'a.deletePreviewImage', function (e) {
            e.preventDefault();
            const $elem = $(this);
            $.ajax({ url: $elem.attr("href"), dataType: 'json' }).done((data) => {
                if (data.code === 200) {
                    $elem.closest("div.image-input").find("img.preview-image").parent().remove();
                    $elem.remove();
                }
                showAlert(ALERT_TYPE, data);
            });
        });
    };

    const init = (config = {}) => {
        const defaultConfig = {
            targetSelector: '.image-input',
            wrapperSelector: '.image-input-wrapper',
            inputSelector: 'input[type="file"]',
            cancelSelector: '[data-js-image-input-action="cancel"]',
            removeSelector: '[data-js-image-input-action="remove"]',
            hiddenInputSelector: 'input[type="hidden"]',
            onInitComplete: null
        };

        const mergedConfig = $.extend({}, defaultConfig, config);
        const $targets = $(mergedConfig.targetSelector);
        const instances = [];

        $targets.each(function () {
            const $target = $(this);

            // Skip if already initialized
            if ($target.data('js-image-input') === true) {
                instances.push({
                    target: $target,
                    elements: getDomElements(mergedConfig, $target),
                    config: mergedConfig
                });
                return;
            }

            const elements = getDomElements(mergedConfig, $target);

            initializeState(elements, mergedConfig);
            setupEventHandlers(elements, mergedConfig);

            instances.push({
                target: $target,
                elements: elements,
                config: mergedConfig
            });
        });

        _initImageTabSwitching();
        _initImageDeleteHandlers();

        if (typeof mergedConfig.onInitComplete === 'function') {
            mergedConfig.onInitComplete(instances);
        }

        return instances;
    };

    /**
     * Destroy an instance and remove event handlers
     */
    const destroy = (element) => {
        const $element = $(element);

        if ($element.data('js-image-input') !== true) return;

        const config = {
            inputSelector: 'input[type="file"]',
            cancelSelector: '[data-js-image-input-action="cancel"]',
            removeSelector: '[data-js-image-input-action="remove"]'
        };

        // Remove event handlers
        $element.find(config.inputSelector).off('change');
        $element.find(config.cancelSelector).off('click');
        $element.find(config.removeSelector).off('click');

        $element.removeData('js-image-input original-src');
        $element.removeAttr('data-js-image-input');

        // Trigger destroy event
        triggerEvent(element[0], 'js.imageinput.destroyed', { element });
    };

    return {
        init: init,
        destroy: destroy
    };
};

const ImageInputModule = createImageInputModule(jQuery);

if (typeof window !== 'undefined' && !window.ImageInputModule) {
    try {
        window.ImageInputModule = ImageInputModule;

        // Auto-initialize on DOM ready with default options
        $(document).ready(function() {
            // Only auto-initialize if not manually initialized elsewhere
            if (!window.ImageInputModuleInitialized) {
                ImageInputModule.init();
                window.ImageInputModuleInitialized = true;
            }
        });
    } catch (e) {
        console.warn('Could not expose ImageInputModule globally:', e);
    }
}

export default ImageInputModule;
